import {
  AllocatableLoadInventorySearchToolbar, AvailableLoadsBy,
  AvailableLoadsByInsight,
  AvailableLoadsByTimeInsight,
  InsightContainer,
  InsightText,
  ResizeableContent,
  RouteBreadcrumbs,
  ThemedPage,
  useApp
} from "@ots-frontend/ots-ui";
import {
  Card,
  Container,
  Grid,
  Group,
  LoadingOverlay,
  Menu,
  Radio,
  rem,
  ScrollArea,
  Stack,
  Text,
  Title,
  UnstyledButton
} from "@mantine/core";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useCallback, useEffect, useMemo, useState} from "react";
import {
  adjustToTimeZone,
  allocatableLoadInventoriesSelector,
  AllocatableLoadInventorySearchCriteria,
  cargoSubtype,
  EntitySearchCriteria,
  getDefaultTimeFrame,
  timeGroup,
  TimeGroup,
  useSearchAllocatableLoadInventoryQuery
} from "@ots-frontend/ots-data";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {useDispatch} from "react-redux";
import {setAllocatableLoadInventorySearchPageFilter} from "../../store/allocatable-load-inventory-search-page/actions";
import {
  getAllocatableLoadInventorySearchPageFilter
} from "../../store/allocatable-load-inventory-search-page/selectors";
import {skipToken} from "@reduxjs/toolkit/query";

export const AvailableLoadsBySearchPage = () => {

  const dispatch = useDispatch()
  const {t} = useTranslation(["translation", "common"]);
  const {regionId, appCategory} = useApp()

  const insightsFilter = useAppSelector(getAllocatableLoadInventorySearchPageFilter)

  const [grouping, setGrouping] = useState<string>("route")
  const [chartType, setChartType] = useState<string>("pie")

  const criteria: AllocatableLoadInventorySearchCriteria | any = useMemo(() => {
    if (insightsFilter?.fromDttm && regionId) {
      const defaultTimeFrame = getDefaultTimeFrame(insightsFilter.fromDttm, insightsFilter.toDttm)

      let criteriaTimeGroup: TimeGroup
      if (defaultTimeFrame === "MONTH" || defaultTimeFrame === "WEEK" || defaultTimeFrame === "DAY") {
        criteriaTimeGroup = timeGroup.daily
      } else {
        criteriaTimeGroup = timeGroup.hourly
      }

      return {
        fromDttm: adjustToTimeZone(insightsFilter.fromDttm).getTime(),
        toDttm: insightsFilter.toDttm ? adjustToTimeZone(insightsFilter.toDttm).getTime() : undefined,
        timeGroup: criteriaTimeGroup.code,
        regionId: regionId,
        cargoSubtypes: [(appCategory?.cargoSubtype ?? cargoSubtype.water).code],
        entityCriteria: insightsFilter.entityCriteria,
      }
    } else {
      return skipToken
    }
  }, [insightsFilter?.fromDttm, insightsFilter?.toDttm, insightsFilter?.entityCriteria, regionId, appCategory?.cargoSubtype])

  const selectAllocatableLoadInventories = useMemo(() => {
    return allocatableLoadInventoriesSelector()
  }, []);

  const {snapshots, isLoading, refetch} = useSearchAllocatableLoadInventoryQuery(criteria, {
    selectFromResult: (result) => ({
      ...result,
      snapshots: selectAllocatableLoadInventories(result),
      isLoading: result.isLoading || result.isFetching,
    })
  });

  const onFilterChange = useCallback((value: {
    fromDate: Date,
    toDate: Date | undefined,
    entityType: string | undefined,
    entityIds: string[] | undefined
  }) => {
    let entityCriteria: EntitySearchCriteria | undefined = undefined
    if (value.entityType) {
      entityCriteria = {
        ids: value.entityIds,
        type: value.entityType
      }
    }

    dispatch(setAllocatableLoadInventorySearchPageFilter({
      fromDttm: value.fromDate.getTime(),
      toDttm: value.toDate?.getTime(),
      entityCriteria: entityCriteria
    }));

    let grouping = "none"

    if (value.entityType) {
      if (value.entityType === "ROUTEGROUP") {
        grouping = "route_group"
      } else if (value.entityType === "ROUTE") {
        grouping = "route"
      } else if (value.entityType === "SITE") {
        grouping = "site"
      } else if (value.entityType === "COMPANY") {
        grouping = "truck_company"
      }
    }


    setGrouping(grouping)

  }, [dispatch]);

  useEffect(() => {
    let entityCriteria: EntitySearchCriteria | undefined = undefined

    if (grouping === "route_group") {
      entityCriteria = {
        type: "ROUTEGROUP"
      }
    } else if (grouping === "route") {
      entityCriteria = {
        type: "ROUTE"
      }
    } else if (grouping === "site") {
      entityCriteria = {
        type: "SITE"
      }
    } else if (grouping === "truck_company") {
      entityCriteria = {
        type: "COMPANY"
      }
    }

    if (entityCriteria?.type !== insightsFilter?.entityCriteria?.type) {
      dispatch(setAllocatableLoadInventorySearchPageFilter({
        ...insightsFilter ?? {fromDttm: new Date().getTime()},
        entityCriteria: entityCriteria
      }));
    }

  }, [dispatch, grouping, insightsFilter])

  const pieData : AvailableLoadsBy = useMemo(() => {
    const record: Record<string, number> = {}

    if (snapshots) {
      for (const snapshot of snapshots) {
        const bucketName = snapshot.objectName ?? t("common.shared.unknown", "Unknown") ?? "Unknown"
        if (!(bucketName in record)) {
          record[bucketName] = 0
        }

        const count = snapshot.entries.reduce((prev, curr) => prev + curr.count, 0)
        record[bucketName] = record[bucketName] + count
      }
    }

    return {
      company: record,
      route: {}
    }
  }, [snapshots, t])

  const total = useMemo(() => {
    return snapshots?.reduce((prev, curr) => {
      const currTotal = curr.entries.reduce((_prev, _curr) => _prev + _curr.count, 0)
      return prev + currTotal
    }, 0)
  }, [snapshots])

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <ScrollArea h={size.height} p="sm" offsetScrollbars={true}>
                <RouteBreadcrumbs p="sm"/>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title
                      order={4}>{t("page.availableloadsby.title", "Insights: Available Loads By", {ns: "translation"})}</Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t("commons.action.plural", "Actions", {ns: "common"})}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("commons.action.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                  <AllocatableLoadInventorySearchToolbar
                    defaultValues={{
                      fromDate: insightsFilter?.fromDttm ? new Date(insightsFilter?.fromDttm) : undefined,
                      toDate: insightsFilter?.toDttm ? new Date(insightsFilter?.toDttm) : undefined,
                      entityType: insightsFilter?.entityCriteria?.type,
                      entityIds: insightsFilter?.entityCriteria?.ids,
                    }}
                    loading={isLoading}
                    onSubmit={onFilterChange}/>
                </Card>

                <LoadingOverlay visible={isLoading ?? false}/>

                <Grid>
                  <Grid.Col span={10}>
                    {chartType === "pie" &&
                      <AvailableLoadsByInsight
                        id={"search_available_loads_by_chart"}
                        title={t<string>("common.insight.availableloads", "Avail. Loads By")}
                        data={pieData}
                        tooltipPos={'left'}
                        containerProps={{
                          h: 400
                        }}/>
                    }
                    {chartType !== "pie" &&
                      <AvailableLoadsByTimeInsight
                        id={"search_rejects_by_time_chart"}
                        fromDttm={insightsFilter?.fromDttm ?? new Date().getTime()}
                        toDttm={insightsFilter?.toDttm}
                        category={appCategory}
                        data={snapshots}
                        type={chartType}
                        stacked={{
                          enabled: true,
                          filled: true
                        }}
                        tooltipPos={'left'}
                        containerProps={{
                          h: 400
                        }}/>
                    }
                  </Grid.Col>
                  <Grid.Col span={2}>
                    <Stack>
                      <InsightContainer
                        title={"Total"}
                        loading={isLoading}>
                        <Stack>
                          <Group align="flex-end" spacing="xs" mt={25}>
                            <InsightText value={total}/>
                          </Group>
                        </Stack>
                      </InsightContainer>
                      <Card shadow="sm" padding="lg" radius="md" withBorder>
                        <Radio.Group
                          name="chartType"
                          value={chartType}
                          onChange={setChartType}>
                          <Stack mt="xs">
                            <Radio value="pie" label={"Pie"}/>
                            <Radio value="line" label={"Line"}/>
                            <Radio value="bar" label={"Bar"}/>
                          </Stack>
                        </Radio.Group>
                      </Card>
                      <Card shadow="sm" padding="lg" radius="md" withBorder>
                        <Radio.Group
                          name="grouping"
                          value={grouping}
                          onChange={setGrouping}>
                          <Stack mt="xs">
                            <Radio value="none" label={t("common.insights.grouping.none", "None")}/>
                            <Radio value="truck_company" label={t("common.insights.grouping.truckcompany", "Hauler")}/>
                            <Radio value="route_group" label={t("common.insights.grouping.routegroup", "Route Group")}/>
                            <Radio value="route" label={t("common.insights.grouping.route", "Route")}/>
                            <Radio value="site" label={t("common.insights.grouping.truckcompany", "Site")}/>
                          </Stack>
                        </Radio.Group>
                      </Card>
                    </Stack>
                  </Grid.Col>
                </Grid>
              </ScrollArea>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
